import React from "react";
import PropTypes from "prop-types";
import {
  Segment,
  Header,
  Icon,
  Grid,
  Table,
  Progress,
  Label,
  Message,
  Loader,
  Statistic,
} from "semantic-ui-react";

// Constants
const COLORS = {
  primary: "#2185d0",
  secondary: "#00b5ad",
  accent: "#f2711c",
  success: "#225745",
  text: "#666",
};

const CARD_STYLE = {
  textAlign: "center",
  padding: "2rem",
  height: "150px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const ICON_STYLE = {
  marginBottom: "1rem",
};

const STATISTIC_VALUE_STYLE = {
  fontSize: "2.5rem",
  marginTop: "-2rem",
};

const STATISTIC_LABEL_STYLE = {
  color: COLORS.text,
  fontSize: "1rem",
};

const getProgressColor = (percentage) => {
  if (percentage > 80) return "red";
  if (percentage > 60) return "orange";
  if (percentage > 40) return "yellow";
  return "green";
};

const StatCard = ({ icon, value, label, color }) => (
  <Grid.Column>
    <Segment style={CARD_STYLE}>
      <Icon name={icon} size="big" style={{ ...ICON_STYLE, color }} />
      <Statistic>
        <Statistic.Value style={{ ...STATISTIC_VALUE_STYLE, color }}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </Statistic.Value>
        <Statistic.Label style={STATISTIC_LABEL_STYLE}>{label}</Statistic.Label>
      </Statistic>
    </Segment>
  </Grid.Column>
);

StatCard.propTypes = {
  icon: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

const DataRow = ({ label, value, color = "blue" }) => (
  <Table.Row>
    <Table.Cell width={8}>
      <Label color={color} size="small">
        {label}
      </Label>
    </Table.Cell>
    <Table.Cell width={8}>
      <strong>{value}</strong>
    </Table.Cell>
  </Table.Row>
);

DataRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  color: PropTypes.string,
};

const OPACUsage = ({ usageStats, statsLoading }) => {
  if (statsLoading) {
    return (
      <Loader active inline="centered">
        Loading usage analytics...
      </Loader>
    );
  }

  const {
    activeUsers,
    totalSessions,
    avgSessionDuration,
    peakUsageHours,
    userEngagement,
    systemPerformance,
  } = usageStats;

  return (
    <Grid stackable>
      {/* Statistics Overview */}
      <Grid.Row>
        <Grid.Column width={16}>
          <Grid>
            <Grid.Row columns={3}>
              <StatCard
                icon="users"
                value={activeUsers}
                label="Active Users"
                color={COLORS.primary}
              />
              <StatCard
                icon="desktop"
                value={totalSessions}
                label="Total Sessions"
                color={COLORS.secondary}
              />
              <StatCard
                icon="clock"
                value={Math.round(avgSessionDuration)}
                label="Avg Duration (min)"
                color={COLORS.accent}
              />
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>

      {/* Peak Usage Hours */}
      <Grid.Row>
        <Grid.Column width={16}>
          <Segment>
            <Header as="h3" style={{ color: COLORS.success }}>
              <Icon name="clock" />
              Peak Usage Hours
            </Header>
            {peakUsageHours.length > 0 ? (
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Time</Table.HeaderCell>
                    <Table.HeaderCell>Searches</Table.HeaderCell>
                    <Table.HeaderCell>Usage Percentage</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {peakUsageHours.map((hour, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>
                        <Icon name="clock" style={{ color: COLORS.success }} />
                        {hour.hour}
                      </Table.Cell>
                      <Table.Cell>
                        <Label color="yellow" size="medium">
                          {hour.searches}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Progress
                          percent={hour.percentage}
                          size="medium"
                          color={getProgressColor(hour.percentage)}
                          label={`${hour.percentage}%`}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            ) : (
              <Message info>No peak usage data available.</Message>
            )}
          </Segment>
        </Grid.Column>
      </Grid.Row>

      {/* User Engagement and System Performance */}
      <Grid.Row>
        <Grid.Column width={16}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <Segment>
                  <Header as="h4" style={{ color: COLORS.success }}>
                    <Icon name="users" />
                    User Engagement
                  </Header>
                  <Table basic>
                    <Table.Body>
                      <DataRow
                        label="New Users"
                        value={userEngagement.newUsers}
                        color="green"
                      />
                      <DataRow
                        label="Returning Users"
                        value={userEngagement.returningUsers}
                        color="blue"
                      />
                      <DataRow
                        label="Bounce Rate"
                        value={`${userEngagement.bounceRate}%`}
                        color="orange"
                      />
                    </Table.Body>
                  </Table>
                </Segment>
              </Grid.Column>

              <Grid.Column width={8}>
                <Segment>
                  <Header as="h4" style={{ color: COLORS.success }}>
                    <Icon name="chart line" /> System Performance
                  </Header>
                  <Table basic>
                    <Table.Body>
                      <DataRow
                        label="Avg Load Time"
                        value={`${systemPerformance.avgLoadTime}ms`}
                        color="teal"
                      />
                      <DataRow
                        label="Uptime"
                        value={`${systemPerformance.uptime}%`}
                        color="green"
                      />
                      <DataRow
                        label="Error Rate"
                        value={`${systemPerformance.errorRate}%`}
                        color="red"
                      />
                    </Table.Body>
                  </Table>
                </Segment>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

OPACUsage.displayName = "OPACUsage";

OPACUsage.propTypes = {
  usageStats: PropTypes.shape({
    activeUsers: PropTypes.number.isRequired,
    totalSessions: PropTypes.number.isRequired,
    avgSessionDuration: PropTypes.number.isRequired,
    peakUsageHours: PropTypes.arrayOf(
      PropTypes.shape({
        hour: PropTypes.string.isRequired,
        searches: PropTypes.number.isRequired,
        percentage: PropTypes.number.isRequired,
      })
    ).isRequired,
    userEngagement: PropTypes.shape({
      newUsers: PropTypes.number.isRequired,
      returningUsers: PropTypes.number.isRequired,
      bounceRate: PropTypes.number.isRequired,
    }).isRequired,
    systemPerformance: PropTypes.shape({
      avgLoadTime: PropTypes.number.isRequired,
      uptime: PropTypes.number.isRequired,
      errorRate: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  statsLoading: PropTypes.bool.isRequired,
};

export default OPACUsage;
